import React from 'react';
import moment from 'moment-timezone';
import {Accordion, Button, Card, Col, Row} from 'react-bootstrap';
import {Glyph} from '../Glyph';
import {SpinnerWrapper} from '../SpinnerWrapper';
import {TIMEZONE} from '../../../utils/Dates';

const ALLOWED_TAGS = new Set(['p', 'ul', 'ol', 'li', 'strong', 'em', 'h3', 'br']);

export function formatSummaryDate(dt) {
    return dt ? moment(dt).tz(TIMEZONE).format('M/D/YYYY h:mm A') : '';
}

export function formatSummaryCategory(category) {
    if (category === 'INCOMING_DOCUMENTS') return 'External Documents';
    if (category === 'Chart Summary') return 'CNS Encounter Summary';
    return category || '';
}

export function sanitizeSummaryHtml(html) {
    if (!html) return '';
    return String(html)
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<\/?([a-zA-Z0-9]+)(\s[^>]*)?\/?>/g, (match, tag) => {
            const name = tag.toLowerCase();
            if (!ALLOWED_TAGS.has(name)) return '';
            if (name === 'br') return '<br>';
            return match.startsWith('</') ? `</${name}>` : `<${name}>`;
        });
}

function summaryPlainText(html) {
    const el = document.createElement('div');
    el.innerHTML = html;
    return el.innerText || el.textContent || '';
}

function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function formatPatientDob(dob) {
    if (!dob) return '';
    const mt = moment(dob);
    return mt.isValid() ? mt.format('M/D/YYYY') : String(dob);
}

function patientClipboardPrefix(patient) {
    if (!patient) return {html: '', plain: ''};
    const fullName = [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim();
    const dob = formatPatientDob(patient.dob);
    const accountNumber = patient.externalPatientId || '';
    const patientNumber = patient.externalPatientId2 || '';
    const lines = [
        ['Patient', fullName],
        ['DOB', dob],
        ['Account Number', accountNumber],
        ['Patient Number', patientNumber]
    ].filter(([, value]) => value);
    if (lines.length === 0) return {html: '', plain: ''};
    return {
        html: `<p>${lines.map(([label, value]) => `<strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}`).join('<br>')}</p>`,
        plain: lines.map(([label, value]) => `${label}: ${value}`).join('\n') + '\n\n'
    };
}

export function copySummary(html, patient) {
    const safe = sanitizeSummaryHtml(html);
    const prefix = patientClipboardPrefix(patient);
    const htmlOut = prefix.html + safe;
    const plain = prefix.plain + summaryPlainText(safe);
    const clipboard = navigator.clipboard;
    try {
        if (clipboard && typeof clipboard.write === 'function' && typeof window.ClipboardItem === 'function') {
            const item = new window.ClipboardItem({
                'text/html': new Blob([htmlOut], {type: 'text/html'}),
                'text/plain': new Blob([plain], {type: 'text/plain'})
            });
            return clipboard.write([item]).catch(() => clipboard.writeText(plain));
        }
        return clipboard.writeText(plain);
    } catch (e) {
        if (clipboard && clipboard.writeText) return clipboard.writeText(plain);
        return Promise.reject(e);
    }
}

export class SummaryCopyButton extends React.Component {
    constructor(props) {
        super(props);
        this.state = {copied: false};
        this.copiedTimer = null;
    }

    componentWillUnmount() {
        window.clearTimeout(this.copiedTimer);
    }

    copy = (event) => {
        event.preventDefault();
        event.stopPropagation();
        copySummary(this.props.html, this.props.patient).then(() => {
            this.setState({copied: true});
            window.clearTimeout(this.copiedTimer);
            this.copiedTimer = window.setTimeout(() => this.setState({copied: false}), 1500);
        }).catch(() => {});
    }

    render() {
        return <div className="interim-summary-copy-anchor no-print">
            <Button type="button" size="sm" variant="light" className="border interim-summary-copy" onClick={this.copy}>
                <Glyph name={this.state.copied ? 'check-lg' : 'clipboard'}/>
                {this.state.copied ? 'Copied' : 'Copy'}
            </Button>
        </div>;
    }
}

export class PatientChartSummaryList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {openKey: null};
    }

    render() {
        const summaries = this.props.summaries || [];
        return <SpinnerWrapper visible={this.props.loading}>
            {summaries.length === 0 ? <div>No CNS encounter summaries</div> :
                <Accordion activeKey={this.state.openKey} onSelect={openKey => this.setState({openKey})}>
                    {summaries.map(summary => {
                        const eventKey = String(summary.id);
                        return <Card key={eventKey}>
                            <Accordion.Toggle as={Card.Header} eventKey={eventKey} className="clickable">
                                <Row>
                                    <Col>{formatSummaryCategory(summary.category)}</Col>
                                    <Col className="text-right">{formatSummaryDate(summary.createdAt)}</Col>
                                </Row>
                            </Accordion.Toggle>
                            <Accordion.Collapse eventKey={eventKey}>
                                <Card.Body className="chart-summary-body">
                                    <SummaryCopyButton html={summary.body} patient={this.props.patient}/>
                                    <div dangerouslySetInnerHTML={{__html: sanitizeSummaryHtml(summary.body)}}/>
                                </Card.Body>
                            </Accordion.Collapse>
                        </Card>;
                    })}
                </Accordion>
            }
        </SpinnerWrapper>;
    }
}
